import { Fragment } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import { QUESTION_GROUP_LOGIC, QUESTION_GROUP_LOGIC_BRANCHES } from '@api/instant_survey';
import { MinusPlusButtons, Pill } from '@utilities';

import GroupLogicRow from './components/GroupLogicRow';
import QuestionLogicOptions from '../../../../../../../QuestionLogic/components/QuestionLogicOptions';
import {
  answerOperators,
  disqualifyOperators,
  skipOperators,
} from '../../../../../../../QuestionLogic/utilities/constants';

import styles from './_index.module.scss';

const BRANCH_CONFIG = {
  disqualify: {
    operators: disqualifyOperators,
    prompt: 'Disqualify if',
  },
  skip: {
    operators: skipOperators,
    prompt: 'Display this question group if',
  },
};

const QuestionGroupLogic = ({
  branch,
  firstErrorRef,
  groupId,
  questionIndex,
  questionList,
  questionLogic,
  setQuestionLogic,
}) => {
  const groupLogic = questionLogic?.[QUESTION_GROUP_LOGIC]?.[groupId]?.[branch];
  const { operators, prompt } = BRANCH_CONFIG[branch];

  const handleAddLogicRow = () => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[QUESTION_GROUP_LOGIC][groupId][branch].logic = [
      ...updatedLogic[QUESTION_GROUP_LOGIC][groupId][branch].logic,
      {
        answers: [],
        operator: answerOperators[0],
        question: {},
      },
    ];
    setQuestionLogic(updatedLogic);
  };

  const getRemovalIndex = (logicRows, index) =>
    Number.isInteger(index) ? index : logicRows.length - 1;

  const handleRemoveLogicRow = (index) => {
    const updatedLogic = cloneDeep(questionLogic);
    const branchLogic = updatedLogic[QUESTION_GROUP_LOGIC][groupId][branch];

    // <= 1 rather than === 1: pruning can leave a branch with zero rows, and the header minus
    // button must still be able to remove it (splice(-1, 1) on an empty array would be a no-op).
    if (branchLogic.logic.length <= 1) {
      // Last logic row being removed - drop this branch, and the group once no branch is left
      delete updatedLogic[QUESTION_GROUP_LOGIC][groupId][branch];
      if (Object.keys(updatedLogic[QUESTION_GROUP_LOGIC][groupId]).length === 0) {
        delete updatedLogic[QUESTION_GROUP_LOGIC][groupId];
      }
    } else {
      branchLogic.logic.splice(getRemovalIndex(branchLogic.logic, index), 1);
    }

    // Clean up the reserved key when no groups have logic left
    if (
      updatedLogic[QUESTION_GROUP_LOGIC] &&
      Object.keys(updatedLogic[QUESTION_GROUP_LOGIC]).length === 0
    ) {
      delete updatedLogic[QUESTION_GROUP_LOGIC];
    }

    setQuestionLogic(updatedLogic);
  };

  const updateOperator = (operator) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[QUESTION_GROUP_LOGIC][groupId][branch].operator = operator;
    setQuestionLogic(updatedLogic);
  };

  if (!groupLogic) {
    return null;
  }

  const groupPillLogic = groupLogic.operator?.value === 'ANY' ? 'OR' : 'AND';

  return (
    <>
      <div className={styles['question-group-logic']}>
        <span className={styles['question-group-logic-text']}>
          <span>{prompt}&nbsp;</span>
          <QuestionLogicOptions
            options={operators}
            optionsLabel="Operator"
            optionsType="radio"
            setValue={updateOperator}
            value={groupLogic.operator}
          />
          <span>&nbsp;apply:</span>
        </span>
        <span className={styles['question-group-logic-minus-plus-buttons']}>
          <MinusPlusButtons
            disablePlusButton={groupLogic.logic.length >= 3}
            hasPlusButton
            isNeutral
            onClickMinus={handleRemoveLogicRow}
            onClickPlus={handleAddLogicRow}
          />
        </span>
      </div>
      {groupLogic.logic.map((_, index) => {
        return (
          <Fragment key={`${groupId}-${branch}-${index}`}>
            <div className={styles['question-group-logic-line']} />
            {index !== 0 && (
              <>
                <Pill
                  className={styles['question-group-logic-pill']}
                  isDisabled
                  value={groupPillLogic}
                />
                <div className={styles['question-group-logic-line']} />
              </>
            )}
            <div className={styles['question-group-logic']}>
              <span className={styles['question-group-logic-text']}>
                <GroupLogicRow
                  branch={branch}
                  firstErrorRef={firstErrorRef}
                  groupId={groupId}
                  index={index}
                  questionIndex={questionIndex}
                  questionList={questionList}
                  questionLogic={questionLogic}
                  setQuestionLogic={setQuestionLogic}
                />
              </span>
              <span className={styles['question-group-logic-minus-plus-buttons']}>
                <MinusPlusButtons
                  hasPlusButton={false}
                  isNeutral
                  onClickMinus={(event) => {
                    event.stopPropagation();
                    handleRemoveLogicRow(index);
                  }}
                />
              </span>
            </div>
          </Fragment>
        );
      })}
    </>
  );
};

QuestionGroupLogic.propTypes = {
  branch: PropTypes.oneOf(QUESTION_GROUP_LOGIC_BRANCHES).isRequired,
  firstErrorRef: PropTypes.object,
  groupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  questionIndex: PropTypes.number.isRequired,
  questionList: PropTypes.array.isRequired,
  questionLogic: PropTypes.object.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
};

export default QuestionGroupLogic;
